import React, { useEffect } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import SubscriptionPlan from '../components/SubscriptionPlan';
import { subscriptionTiers } from '../services/subscriptionService';
import '../styles/SubscriptionPage.css';

const SubscriptionPage = () => {
  const { subscription, loading, upgradeSubscription, manageSubscription, refreshSubscription } = useSubscription();
  const { user, loading: authLoading } = useAuth();

  // Debug logging
  console.log('SubscriptionPage - Current user:', user);
  console.log('SubscriptionPage - Current subscription:', subscription);
  console.log('SubscriptionPage - cancelAtPeriodEnd:', subscription?.cancelAtPeriodEnd);

  // Check for successful checkout and refresh subscription data
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      // Remove the success parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh subscription data after successful checkout
      setTimeout(() => {
        refreshSubscription();
      }, 2000); // Wait 2 seconds for webhook processing
    }
  }, [refreshSubscription]);

  const handleUpgrade = async (tier) => {
    try {
      if (tier === 'FREE') {
        // Handle downgrade to free tier
        const confirmed = window.confirm(
          'Cancel your subscription and return to the Free tier?\n\n' +
          '✅ You\'ll keep full access until your current billing period ends.\n' +
          '✅ No refund needed - you\'ll receive the full value of what you paid for.\n' +
          '✅ You can reactivate anytime before your subscription expires.\n\n' +
          'This will open the customer portal where you can manage your cancellation.'
        );
        if (confirmed) {
          // Open Stripe customer portal to manage subscription
          await manageSubscription();
        }
        return;
      }
      
      // Check if it's actually an upgrade or downgrade
      const currentTier = subscription?.tier || 'FREE';
      const tierPrices = { FREE: 0, PREMIUM: 15, PRO: 50 };
      const isUpgrade = tierPrices[tier] > tierPrices[currentTier];
      const action = isUpgrade ? 'Upgrade' : 'Downgrade';
      
      const tierInfo = subscriptionTiers[tier];
      let confirmMessage = `${action} to ${tierInfo.name} for $${tierInfo.price}/month?\n\n` +
        `You'll get:\n` +
        `• ${tierInfo.propertyAnalyses === -1 ? 'Unlimited' : tierInfo.propertyAnalyses} property analyses\n` +
        `• ${tierInfo.chatMessages === -1 ? 'Unlimited' : tierInfo.chatMessages} chat messages\n` +
        `• ${tierInfo.features.join('\n• ')}`;
      
      // Add warning for downgrades
      if (!isUpgrade && action === 'Downgrade') {
        confirmMessage += `\n\n⚠️ IMPORTANT: Your limits will change immediately after downgrading, regardless of when your current billing cycle ends. You will not receive a refund for the remaining time in your current plan.`;
      }
      
      const confirmed = window.confirm(confirmMessage);
      
      if (confirmed) {
        // Redirect to Stripe checkout
        await upgradeSubscription(tier);
      }
    } catch (error) {
      console.error('Error processing subscription change:', error);
      alert('Failed to process subscription change. Please try again.');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="subscription-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading subscription information...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="subscription-page">
        <div className="subscription-header">
          <h1>Authentication Required</h1>
          <p>Please log in to view and manage your subscription plans.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-header">
        <h1>Choose Your Plan</h1>
        <p>Select the plan that best fits your property investment needs</p>
      </div>

      {/* Cancellation Notice */}
      {subscription?.cancelAtPeriodEnd && (
        <div className="cancellation-notice">
          <h3>⚠️ Subscription Ending</h3>
          <p>
            Your subscription is scheduled to end on{' '}
            <strong>
              {subscription.currentPeriodEnd 
                ? new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()
                : 'your next billing date'
              }
            </strong>
            . You'll continue to have full access until then.
          </p>
          <p>Want to keep your subscription? You can reactivate it anytime through the customer portal.</p>
          <button 
            className="manage-subscription-btn secondary"
            onClick={manageSubscription}
          >
            Reactivate Subscription
          </button>
        </div>
      )}

      <div className="subscription-plans">
        {Object.entries(subscriptionTiers).map(([tierKey, tierData]) => (
          <SubscriptionPlan
            key={tierKey}
            tier={tierKey}
            isCurrentPlan={subscription?.tier === tierKey}
            onUpgrade={handleUpgrade}
          />
        ))}
      </div>

      {subscription?.tier && subscription.tier !== 'FREE' && (
        <div className="subscription-management">
          <h3>Manage Your Subscription</h3>
          <p>Update payment methods, view invoices, or cancel your subscription.</p>
          <button 
            className="manage-subscription-btn"
            onClick={manageSubscription}
          >
            Manage Subscription
          </button>
        </div>
      )}

      <div className="subscription-footer">
        <div className="faq-section">
          <h3>Frequently Asked Questions</h3>
          
          <div className="faq-item">
            <h4>Can I change my plan anytime?</h4>
            <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
          </div>
          
          <div className="faq-item">
            <h4>What happens if I exceed my limits?</h4>
            <p>You'll be prompted to upgrade your plan. Your account won't be suspended, but additional usage may be restricted.</p>
          </div>
          
          <div className="faq-item">
            <h4>Is there a free trial?</h4>
            <p>Yes! The Free tier gives you 5 property analyses and 10 chat messages per month to try our features.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;